import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { storage } from '../firebase';
import {
  ref as storageRef,
  uploadBytes,
  getDownloadURL,
  deleteObject,
} from 'firebase/storage';
import { v4 as uuidv4 } from 'uuid';
import { useUser } from '../contexts/UserContext';

const ProfilePhotoContext = createContext();
export const useProfilePhoto = () => useContext(ProfilePhotoContext);

export const ProfilePhotoProvider = ({ children }) => {
  const [photos, setPhotos] = useState([]);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState(0);
  const [isEditMode, setIsEditMode] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [zoom, setZoom] = useState(50);
  const [rotation, setRotation] = useState(0);
  const [loading, setLoading] = useState(false);

  const fileInputRef = useRef(null);

  const maxPhotos = 4;
  const maxBytes = 5 * 1024 * 1024;
  const allowedFormats = ['jpg', 'jpeg', 'png', 'bmp', 'gif'];

  const {
    updateUserPicture,
    listUserPhotos,
    addUserPhotoRecord,
    deleteUserPhotoRecord,
    showAlert,
  } = useUser();

  const isValidImage = (file) => {
    const ext = (file?.name || '').split('.').pop().toLowerCase();
    return allowedFormats.includes(ext);
  };

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      try {
        const existing = await listUserPhotos();
        if (!mounted) return;

        const normalized = (existing || []).map((p) => ({
          id: p.id,
          fileName: p.file_name,
          src: p.url,
          createdAt: p.created_at,
        }));

        setPhotos(normalized);
        setSelectedPhotoIndex(normalized.length ? 0 : 0);
      } catch (e) {
        console.error('[ProfilePhoto] load error:', e);
        showAlert('אירעה שגיאה בטעינת התמונות', 'error');
      } finally {
        if (mounted) setLoading(false);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [listUserPhotos, showAlert]);

  const addPhoto = async (file) => {
    if (!file) return;

    if (photos.length >= maxPhotos) {
      showAlert(`ניתן לשמור עד ${maxPhotos} תמונות פרופיל`, 'error');
      return;
    }

    if (!isValidImage(file)) {
      showAlert('פורמט תמונה לא חוקי. ניתן להעלות JPG, JPEG, PNG, BMP או GIF בלבד', 'error');
      return;
    }

    if (file.size > maxBytes) {
      showAlert('הקובץ גדול מ־5MB', 'error');
      return;
    }

    setLoading(true);

    const ext = file.name.split('.').pop().toLowerCase();
    const storageName = `${uuidv4()}.${ext}`;
    const blobRef = storageRef(storage, `profile_photos/${storageName}`);

    try {
      await uploadBytes(blobRef, file);
      const url = await getDownloadURL(blobRef);

      const id = uuidv4();
      const saved = await addUserPhotoRecord({
        id,
        file_name: storageName,
        url,
      });

      const newPhoto = {
        id: saved?.id || id,
        fileName: saved?.file_name || storageName,
        src: saved?.url || url,
        createdAt: saved?.created_at,
      };

      setPhotos((prev) => {
        const updated = [...prev, newPhoto];
        setSelectedPhotoIndex(updated.length - 1);
        return updated;
      });

      showAlert('התמונה הועלתה בהצלחה', 'success');
    } catch (err) {
      console.error('[ProfilePhoto] upload error:', err);
      showAlert(err?.message || 'שגיאה בהעלאת התמונה', 'error');

      try {
        await deleteObject(blobRef);
      } catch {}
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) addPhoto(file);
    e.target.value = '';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) addPhoto(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (!isDragging) setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  const handleDelete = async (id) => {
    const photo = photos.find((p) => p.id === id);
    if (!photo) return;

    setLoading(true);
    try {
      const blobRef = storageRef(storage, `profile_photos/${photo.fileName}`);
      await deleteObject(blobRef);
      await deleteUserPhotoRecord(id);

      const updated = photos.filter((p) => p.id !== id);
      setPhotos(updated);
      setSelectedPhotoIndex((idx) => {
        if (updated.length === 0) return 0;
        return Math.min(idx, updated.length - 1);
      });

      showAlert('התמונה נמחקה בהצלחה', 'success');
    } catch (err) {
      console.error('[ProfilePhoto] delete error:', err);
      showAlert('מחיקת התמונה נכשלה', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleNext = () => {
    if (photos.length <= 1) return;
    setSelectedPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const handlePrevious = () => {
    if (photos.length <= 1) return;
    setSelectedPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  const handleApplyPhoto = async () => {
    const selected = photos[selectedPhotoIndex];
    if (!selected) return;

    setLoading(true);
    try {
      await updateUserPicture(selected.src);
      showAlert('תמונת הפרופיל עודכנה בהצלחה', 'success');
    } catch (e) {
      console.error('[ProfilePhoto] apply error:', e);
      showAlert('שגיאה בעדכון תמונת הפרופיל', 'error');
    } finally {
      setLoading(false);
    }
  };

  const value = useMemo(
    () => ({
      photos,
      selectedPhotoIndex,
      isEditMode,
      previewMode,
      isDragging,
      zoom,
      rotation,
      loading,
      maxPhotos,
      fileInputRef,
      setZoom,
      setRotation,
      setIsEditMode,
      setPreviewMode,
      setIsDragging,
      setSelectedPhotoIndex,
      handleUploadClick,
      handleFileChange,
      handleDrop,
      handleDragOver,
      handleDragLeave,
      handleDelete,
      handleNext,
      handlePrevious,
      handleApplyPhoto,
    }),
    [
      photos,
      selectedPhotoIndex,
      isEditMode,
      previewMode,
      isDragging,
      zoom,
      rotation,
      loading,
    ]
  );

  return (
    <ProfilePhotoContext.Provider value={value}>
      {children}
    </ProfilePhotoContext.Provider>
  );
};